/** Canvas contextual anchored popover — the ONE primitive every
 * "choice with a Cancel" interaction on the Canvas should render
 * through (VISTOERA_CONTENT_CANVAS_MEDIA_NODE_HOVER_ACTIONS_CONNECTION_PORTS_POPOVER_AND_PLACEMENT_P1
 * §29–§34), replacing `Modal.confirm`'s canvas-center dialog for
 * "重新生成" (spec §28/§29). Anchored to the triggering button's own
 * `getBoundingClientRect()` — never the viewport/canvas center — with
 * automatic flip when there isn't enough room above/below, and three
 * equally-weighted cancel paths (×, outside click, Esc — spec §31) that
 * are ALL wired to the exact same `onClose`, so there is only ever one
 * way for a caller to accidentally give Cancel a side effect: actually
 * calling something other than `onClose` from inside one of them (which
 * none of this file's own JSX does).
 *
 * Portals into the nearest `.pcv-root` ancestor of the anchor element
 * (falling back to `document.body`) rather than always using
 * `document.body` — `.pcv-root` is where every `--pcv-*` theme token
 * lives (spec §35 "不能硬编码纯白 modal"), and CSS custom properties only
 * cascade to DESCENDANTS, so portalling outside `.pcv-root` would silently
 * lose dark/light theming. `position: fixed` + viewport coordinates means
 * WHERE in the DOM tree it mounts never affects its on-screen position
 * (`.pcv-root` has no `transform`/`filter` of its own to create a
 * different fixed-position containing block). */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useCanvasAnchoredReposition } from './canvasAnchoredOverlay'

interface CanvasActionPopoverProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  /** Popover width in px — kept small/fixed (spec §30 "尺寸：紧凑") rather
   * than measuring content, so positioning math never needs a second
   * measure-then-reposition pass. */
  width?: number
}

const GAP = 8
const VIEWPORT_MARGIN = 12

export default function CanvasActionPopover({ anchorEl, open, onClose, title, children, width = 240 }: CanvasActionPopoverProps) {
  // VISTOERA_SAAS_I18N_FOUNDATION V1 — Canvas common actions reference
  // migration (spec §26/§29). This primitive's own chrome (default
  // aria-label, close button) now follows the current UI Locale; any
  // domain-specific `title` a caller passes in remains that caller's own
  // responsibility (legacy debt tracked per-caller, not fixed here).
  const { t } = useTranslation('canvas')
  const popRef = useRef<HTMLDivElement | null>(null)
  const [style, setStyle] = useState<{ top: number; left: number; flipped: boolean } | null>(null)

  // Position (and re-position on scroll/resize while open) purely from
  // the anchor's live `getBoundingClientRect()` — never a value cached
  // at open-time, so a Canvas pan/zoom while the popover is open can't
  // leave it pointing at empty space.
  useLayoutEffect(() => {
    if (!open || !anchorEl) return
    const reposition = () => {
      const rect = anchorEl.getBoundingClientRect()
      const popHeight = popRef.current?.offsetHeight ?? 120
      const spaceAbove = rect.top
      const preferAbove = spaceAbove >= popHeight + GAP + VIEWPORT_MARGIN
      const top = preferAbove ? rect.top - popHeight - GAP : rect.bottom + GAP
      let left = rect.left
      const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN
      if (left > maxLeft) left = Math.max(VIEWPORT_MARGIN, maxLeft)
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN
      setStyle({ top: Math.max(VIEWPORT_MARGIN, top), left, flipped: !preferAbove })
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, anchorEl, width])

  useCanvasAnchoredReposition(open, () => {
    if (!anchorEl) return
    const rect = anchorEl.getBoundingClientRect()
    const popHeight = popRef.current?.offsetHeight ?? 120
    const spaceAbove = rect.top
    const preferAbove = spaceAbove >= popHeight + GAP + VIEWPORT_MARGIN
    const top = preferAbove ? rect.top - popHeight - GAP : rect.bottom + GAP
    let left = rect.left
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN
    if (left > maxLeft) left = Math.max(VIEWPORT_MARGIN, maxLeft)
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN
    setStyle({ top: Math.max(VIEWPORT_MARGIN, top), left, flipped: !preferAbove })
  })

  // Outside click + Esc — both plain `onClose`, no side effects of any
  // kind (spec §32 "Cancel 不产生任何副作用").
  useEffect(() => {
    if (!open) return
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (popRef.current?.contains(target)) return
      if (anchorEl?.contains(target)) return
      onClose()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open, anchorEl, onClose])

  if (!open || !anchorEl) return null
  const container = anchorEl.closest('.pcv-root') || document.body

  return createPortal(
    <div
      ref={popRef}
      className="pcv-action-popover nodrag nowheel"
      style={{ top: style?.top ?? -9999, left: style?.left ?? -9999, width, visibility: style ? 'visible' : 'hidden' }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-label={typeof title === 'string' ? title : t('actionPopover.defaultTitle')}
    >
      <div className="pcv-action-popover-header">
        {title ? <span className="pcv-action-popover-title">{title}</span> : <span />}
        <button
          type="button"
          className="pcv-action-popover-close"
          aria-label={t('actionPopover.closeAriaLabel')}
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="pcv-action-popover-body">{children}</div>
    </div>,
    container,
  )
}
